import { Injectable } from '@angular/core';
import { MatLegacyDialogRef, MatLegacyDialogState } from '@angular/material/legacy-dialog';
import { PdfExportFacade } from '@mode/report-viewer/pdf-export/domain';
import { PdfExportDomain } from '@mode/shared/contract-common';
import { DialogService } from '@mode/shared/ui';
import { AnalyticsEvent, AnalyticsService } from '@mode/shared/util-segment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { PdfExportDialogComponent } from './pdf-export-dialog.component';

import { ReportViewerFacade } from '@mode/report-viewer/domain';
import { isPresent } from '@mode/shared/util-js';
import { ObservabilityEvents, ObservabilityService } from '@mode/shared/util-observability';

enum PDFStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  CANCELED = 'canceled',
  CLOSED = 'closed',
}

@Injectable()
export class PdfExportDialogService {
  private dialogRef: MatLegacyDialogRef<PdfExportDialogComponent> | null = null;
  public job: PdfExportDomain.PdfExportJob | undefined;

  private titleNotifier = new BehaviorSubject<string | null>(null);
  public title$: Observable<string>;

  constructor(
    public pdfExportFacade: PdfExportFacade,
    public reportViewerFacade: ReportViewerFacade,
    public dialogService: DialogService,
    private analyticsService: AnalyticsService,
    private observabilityService: ObservabilityService
  ) {
    this.title$ = this.titleNotifier.pipe(filter(isPresent));

    pdfExportFacade.openPdfExportModal$
      .pipe(
        filter(() => this.dialogRef?.getState() !== MatLegacyDialogState.OPEN),
        withLatestFrom(reportViewerFacade.token$, reportViewerFacade.displayName$)
      )
      .subscribe(([, reportToken, name]) => {
        this.openPdfExportModal(reportToken, name);
      });
  }

  public openPdfExportModal(reportToken: string, title: string) {
    if (this.pdfExportFacade.currentExportJob == null) {
      // TODO: Should error out here
    }

    this.titleNotifier.next(title);
    const destroy$ = new Subject<void>();
    this.dialogRef = this.dialogService.open({
      component: PdfExportDialogComponent,
      matDialogConfig: {
        data: {
          job: this.pdfExportFacade.currentExportJob,
        },
      },
    });

    this.dialogRef?.componentInstance.pdfGenerationSuccess
      .pipe(
        take(1),
        tap(() => this._handleSuccess(reportToken))
      )
      .subscribe();

    this.dialogRef?.componentInstance.pdfGenerationFailed
      .pipe(
        take(1),
        tap(() => this._handleError(reportToken))
      )
      .subscribe();

    this.dialogRef?.componentInstance.pdfDownloaded
      .pipe(
        takeUntil(destroy$),
        tap(() => this._handleDownloadClicked())
      )
      .subscribe();

    this.dialogRef?.componentInstance.cancelled
      .pipe(
        take(1),
        tap(() => this._handleCancelClicked(reportToken))
      )
      .subscribe();

    this.dialogRef?.componentInstance.pdfIsGenerating
      .pipe(
        take(1),
        tap(() => this._handleLoading(reportToken))
      )
      .subscribe();

    this.dialogRef
      .afterClosed()
      .pipe(
        tap(() => {
          this.markPDFExportFinishedMeasurement(reportToken, PDFStatus.CLOSED);
          destroy$.next();
          destroy$.complete();
        })
      )
      .subscribe();
  }

  close() {
    this.dialogService.closeAll();
  }

  public markPDFExportStartTrace() {
    this.observabilityService.mark(ObservabilityEvents.PDF_EXPORT);
  }

  public markPDFExportFinishedMeasurement(reportToken: string, pdfStatus: string) {
    this.observabilityService.recordPageTiming(ObservabilityEvents.PDF_EXPORT, {
      'mode.webapp_ui.report_token': reportToken,
      'mode.webapp_ui.report_capture': true,
      'mode.webapp_ui.rc_ui_pdf_export_status': pdfStatus,
    });
  }

  private _handleCancelClicked(reportToken: string) {
    this._segmentUIClickedDetails('Cancel');
    this.markPDFExportFinishedMeasurement(reportToken, PDFStatus.CANCELED);
  }

  private _handleDownloadClicked() {
    this._segmentUIClickedDetails('Download');
  }

  private _handleSuccess(reportToken: string) {
    this._segmentModalShownDetails('PDF is ready', reportToken);
    this.markPDFExportFinishedMeasurement(reportToken, PDFStatus.SUCCESS);
  }

  private _handleLoading(reportToken: string) {
    this._segmentModalShownDetails('Generating PDF', reportToken);
    this.markPDFExportStartTrace();
  }

  private _handleError(reportToken: string) {
    this._segmentModalShownDetails('PDF Failed to Generate', reportToken);
    this.markPDFExportFinishedMeasurement(reportToken, PDFStatus.FAILED);
  }

  private _segmentUIClickedDetails(action: string) {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: 'Export PDF',
      action,
    });
  }

  private _segmentModalShownDetails(modalTypeMessage: string, reportToken: string) {
    this.analyticsService.track(AnalyticsEvent.Name.ModalShown, {
      location: 'Report View',
      modal_type: modalTypeMessage,
      context: 'report',
      context_id: reportToken,
    });
  }
}
